import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApplicationContract } from '../../contracts/application';
import { ScopeContract } from '../../contracts/scope';

export interface ApplicationsState {
    applications: {
        byId: {
            [key: string]: ApplicationContract;
        };
        ids: string[];
    };
    scopes: ScopeContract[];
    environment: 'sandbox' | 'production';
    loading: boolean;
    error: string | null;
    credentialsBeingCreated: boolean;
}

export const initialApplicationsState: ApplicationsState = {
    applications: {
        byId: {},
        ids: [],
    },
    scopes: [],
    environment: 'sandbox',
    loading: false,
    error: null,
    credentialsBeingCreated: false,
};

const slice = createSlice({
    name: 'Applications',
    initialState: initialApplicationsState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.loading = false;
        },

        setApplications: (state, action: PayloadAction<ApplicationContract[]>) => {
            state.applications.byId = {};
            state.applications.ids = [];
            
            action.payload.forEach(app => {
                state.applications.byId[app.id] = app;
                state.applications.ids.push(app.id);
            });
            
            state.loading = false;
            state.error = null;
        },

        setScopes: (state, action: PayloadAction<ScopeContract[]>) => {
            state.scopes = action.payload;
        },

        addApplication: (state, action: PayloadAction<ApplicationContract>) => {
            const app = action.payload;
            state.applications.byId[app.id] = app;
            state.applications.ids.unshift(app.id);
        },

        updateApplication: (state, action: PayloadAction<ApplicationContract>) => {
            const app = action.payload;
            if (state.applications.byId[app.id]) {
                state.applications.byId[app.id] = app;
            }
        },

        removeApplication: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            delete state.applications.byId[id];
            state.applications.ids = state.applications.ids.filter(appId => appId !== id);
        },

        setEnvironment: (state, action: PayloadAction<'sandbox' | 'production'>) => {
            state.environment = action.payload;
            state.applications = { byId: {}, ids: [] }; // Clear old environment data
            state.error = null;
        },

        setCredentialsBeingCreated: (state, action: PayloadAction<boolean>) => {
            state.credentialsBeingCreated = action.payload;
        },
    },
});

export const {
    setLoading,
    setError,
    setApplications,
    setScopes,
    addApplication,
    updateApplication,
    removeApplication,
    setEnvironment,
    setCredentialsBeingCreated,
} = slice.actions;

export default slice.reducer;
